import { useState } from 'react';
import { ArrowLeft, BarChart3, Users, Globe, TrendingUp } from 'lucide-react';
import { useAnalytics } from '../hooks/useAnalytics';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { UserAssetsModal } from '../components/modals/UserAssetsModal';


export const AnalyticsPage = () => {
  const { 
    users, 
    summary, 
    loading, 
    error,
    refetch
  } = useAnalytics();

  const [selectedUser, setSelectedUser] = useState<{ id: string; name: string } | null>(null);

  const goBack = () => {
    window.history.back();
  };



  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <LoadingSpinner size="lg" />
          <p className="text-th-secondary">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-elevated border border-subtle rounded-lg p-8 max-w-md w-full text-center">
          <div className="text-status-error text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-th-primary mb-2">Analytics Error</h2>
          <p className="text-th-secondary mb-4">{error}</p>
          <button onClick={refetch} className="button-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="border-b border-subtle bg-surface sticky top-0 z-30">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between max-w-6xl mx-auto">
            <div className="flex items-center space-x-3">
              <button
                onClick={goBack}
                className="p-2 text-th-secondary hover:text-th-primary transition-colors rounded-lg hover:bg-overlay"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-brand rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-th-primary">Analytics Dashboard</h1>
                  <p className="text-xs text-th-secondary">Platform insights and data</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-6 h-[calc(100vh-80px)] overflow-y-auto">
        {/* Summary Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="card p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-brand/20 rounded-lg">
                <Users className="w-6 h-6 text-brand" />
              </div>
              <div>
                <p className="text-th-secondary text-sm">Total Users</p>
                <p className="text-2xl font-bold text-brand">{summary.total_users}</p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-accent/20 rounded-lg">
                <TrendingUp className="w-6 h-6 text-accent" />
              </div>
              <div>
                <p className="text-th-secondary text-sm">Total Assets</p>
                <p className="text-2xl font-bold text-accent">{summary.total_assets}</p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-status-warning/20 rounded-lg">
                <Globe className="w-6 h-6 text-status-warning" />
              </div>
              <div>
                <p className="text-th-secondary text-sm">Countries</p>
                <p className="text-2xl font-bold text-status-warning">{summary.total_countries}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-th-primary mb-4">All Users</h3>
          
          {loading ? (
            <div className="text-center py-8">
              <LoadingSpinner size="lg" />
              <p className="text-th-secondary mt-4">Loading users...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-16 h-16 text-th-secondary mx-auto mb-4" />
              <h4 className="text-th-primary font-medium mb-2">No Users Found</h4>
              <p className="text-th-secondary text-sm">No user data available</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface border-b border-subtle">
                  <tr className="text-left">
                    <th className="px-4 py-3 text-xs font-medium text-th-secondary uppercase tracking-wider">Name</th>
                    <th className="px-4 py-3 text-xs font-medium text-th-secondary uppercase tracking-wider">User ID</th>
                    <th className="px-4 py-3 text-xs font-medium text-th-secondary uppercase tracking-wider">Country</th>
                    <th className="px-4 py-3 text-xs font-medium text-th-secondary uppercase tracking-wider">Assets</th>
                    <th className="px-4 py-3 text-xs font-medium text-th-secondary uppercase tracking-wider">Joined</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-hyperBorder">
                  {users.map((user) => (
                    <tr key={user.hy_user_id} className="hover:bg-overlay transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-brand rounded-full flex items-center justify-center">
                            <span className="text-white text-xs font-bold">
                              {user.hy_name?.charAt(0).toUpperCase() || 'U'}
                            </span>
                          </div>
                          <span className="text-th-primary font-medium">{user.hy_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-th-secondary font-mono text-xs">
                          {user.hy_user_id.slice(0, 8)}...
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-2">
                          <Globe className="w-3 h-3 text-status-warning" />
                          <span className="text-th-primary">{user.hy_country}</span>
                          <span className="text-th-secondary text-xs">({user.hy_country_code})</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setSelectedUser({ id: user.hy_user_id, name: user.hy_name })}
                          className="text-brand font-semibold hover:text-brand/80 underline transition-colors"
                        >
                          {user.total_assets}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-th-secondary text-sm">
                          {new Date(user.created_at).toLocaleDateString()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* User Assets Modal */}
      {selectedUser && (
        <UserAssetsModal
          isOpen={!!selectedUser}
          onClose={() => setSelectedUser(null)}
          userId={selectedUser.id}
          userName={selectedUser.name}
        />
      )}

      {/* Background Effects */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
        <div className="absolute top-3/4 left-1/2 w-80 h-80 bg-status-warning/5 rounded-full blur-3xl" />
      </div>
    </div>
  );
};